<script setup lang="ts">
import {onBeforeUnmount, onMounted, ref, toRaw} from 'vue';
import {useCurrentLangStore} from '~/stores/pageLangStore';
import {useI18n} from 'vue-i18n';

const config = useRuntimeConfig();

const isOpen = ref('hidden');
const items = ref([]);
const itemsTopMenu = ref([]);
const itemsSocialMedia = ref([]);
const showDropdown = ref(false);
const selectedLocale = ref('pt-BR');
const isScrolled = ref(false);

const currentLangStore = useCurrentLangStore();

let {data}: any = await useFetch(
    () => `${config.public.baseUrlBackend}/api/menu/Acesso Rápido`,
    {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
    }
);

let {data: topMenu}: any = await useFetch(
    () => `${config.public.baseUrlBackend}/api/menu/Empresas Parceiras`,
    {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
    }
);

let {data: socialMedia}: any = await useFetch(
    () => `${config.public.baseUrlBackend}/api/footer`,
    {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
    }
);

items.value = toRaw(data.value);
itemsTopMenu.value = toRaw(topMenu.value);
itemsSocialMedia.value = toRaw(socialMedia.value.socialMedia.links);

const getLocaleLabel = (locale: string) => {
    switch (locale) {
        case 'pt-BR':
            return 'Português';
        case 'es-ES':
            return 'Espanhol';
        case 'en-US':
            return 'Inglês';
        default:
            return '';
    }
};

const {locale, locales} = useI18n();
const availableLocales: string[] = locales.value.map((item) => item.code);
selectedLocale.value = locale.value;

const changeLang = (lang: string) => {
    showDropdown.value = false;
    currentLangStore.setCurrentLang(lang);
    selectedLocale.value = lang;
};

const getIconUrl = (selectedLocale: string) => {
    const icons = {
        'pt-BR': '/images/br.svg',
        'es-ES': '/images/es.svg',
        'en-US': '/images/us.svg',
    };
    return icons[selectedLocale] || '';
};

const toggleAccordion = () => {
    isOpen.value = isOpen.value === 'hidden' ? 'block' : 'hidden';
};

const mobileDropdownRef = ref<HTMLElement | null>(null);

const handleMobileClickOutside = (event: MouseEvent) => {
    if (
        isOpen.value === 'block' &&
        mobileDropdownRef.value &&
        !mobileDropdownRef.value.contains(event.target as Node)
    ) {
        isOpen.value = 'hidden';
    }
};

const blueBarRef = ref<HTMLElement | null>(null);
const whiteBarRefMobile = ref<HTMLElement | null>(null);
const whiteBarRefDesktop = ref<HTMLElement | null>(null);
const blueBarHeight = ref(0);
const whiteBarHeight = ref(0);

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('click', handleMobileClickOutside);

    const savedLang = localStorage.getItem('currentLang');
    if (savedLang) {
        currentLangStore.setCurrentLang(savedLang);
        locale.value = savedLang;
    }
    selectedLocale.value = currentLangStore.currentValue || locale.value;

    window.addEventListener('scroll', handleScroll);

    // Calcular a altura da barra azul
    if (blueBarRef.value) {
        blueBarHeight.value = blueBarRef.value.offsetHeight;
    }

    // Calcular a altura da barra branca (mobile)
    if (whiteBarRefMobile.value) {
        whiteBarHeight.value = whiteBarRefMobile.value.offsetHeight;
    }

    // Calcular a altura da barra branca (desktop)
    if (whiteBarRefDesktop.value) {
        whiteBarHeight.value = whiteBarRefDesktop.value.offsetHeight;
    }
});

onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll);
    document.removeEventListener('click', handleClickOutside);
    document.removeEventListener('click', handleMobileClickOutside);
});

const handleScroll = () => {
    isScrolled.value = window.scrollY > 45;
    isOpen.value = 'hidden'
};

const toggleDropdown = () => {
    showDropdown.value = !showDropdown.value;
};

const dropdownRef = ref<HTMLElement | null>(null);

const handleClickOutside = (event: MouseEvent) => {
    if (
        showDropdown.value &&
        dropdownRef.value &&
        !dropdownRef.value.contains(event.target as Node)
    ) {
        showDropdown.value = false;
    }
};

const handleMenuClick = () => {
    isOpen.value = 'hidden';
};
</script>

<template>
    <nav
        :class="[
            isScrolled ? 'mb-[70px] lg:mb-0' : '',
        ]"
    >
        <!-- Barra azul superior -->
        <div
            v-show="!isScrolled"
            ref="blueBarRef"
            class="bg-[#0400A1] h-9 lg:h-12 xl:h-13 flex justify-between items-center w-full"
        >
            <div class="container mx-auto flex justify-between items-center">
                <ul class="flex items-center space-x-4 ml-6 lg:hidden">
                    <li v-for="item in itemsSocialMedia" :key="item.id">
                        <a :href="item.link" target="_blank">
                            <NuxtImg :src="item.icon" :alt="item.name" class="w-4"/>
                        </a>
                    </li>
                </ul>

                <!-- Empresas parceiras -->
                <div
                    class="hidden lg:flex items-center space-x-8 xl:space-x-12 3xl:space-x-24 mr-6 ml-16 lg:ml-20 3xl:ml-52"
                >
                    <a v-for="itemTop in itemsTopMenu.links" :key="itemTop.link" :href="itemTop.link">
                        <img
                            :src="itemTop.icon"
                            :alt="'Logo ' + itemTop.name"
                            class="w-auto h-auto"
                        />
                    </a>
                </div>

                <div class="flex items-center justify-end mr-6 lg:mr-20 gap-16">
                    <div class="hidden xl:flex items-center space-x-4">
                        <a
                            href="https://www.unochapeco.edu.br/webmail"
                            class="flex items-center space-x-2 text-white"
                        >
                            <img src="/assets/icons/header/mail-w.svg" alt="Webmail" class="h-7"/>
                            <p>Webmail</p>
                        </a>
                        <a
                            href="https://www.unochapeco.edu.br/minhauno"
                            class="flex items-center space-x-2 text-white"
                        >
                            <img src="/assets/icons/header/user-w.svg" alt="Minha Uno" class="h-5"/>
                            <p class="whitespace-nowrap">Minha Uno</p>
                        </a>
                    </div>

                    <!-- Dropdown do idioma -->
                    <div ref="dropdownRef" class="relative inline-block">
                        <button
                            @click="toggleDropdown"
                            class="flex items-center space-x-1 focus:outline-hidden"
                        >
                            <img
                                :src="getIconUrl(selectedLocale)"
                                alt="Flag"
                                class="w-5 lg:w-6 xl:w-7"
                            />
                            <img src="/assets/icons/down-w.svg" alt="Down Icon" class="w-5 lg:w-6 xl:w-7"/>
                        </button>
                        <transition
                            enter-active-class="transition duration-300 ease-out transform"
                            enter-from-class="opacity-0 scale-95"
                            enter-to-class="opacity-100 scale-100"
                            leave-active-class="transition duration-200 ease-in transform"
                            leave-from-class="opacity-100 scale-100"
                            leave-to-class="opacity-0 scale-95"
                        >
                            <div
                                v-if="showDropdown"
                                class="
                                    origin-top-right
                                    absolute
                                    right-0
                                    mt-1 w-36 lg:w-40
                                    rounded-md
                                    shadow-lg
                                    bg-white
                                    ring-1 ring-black ring-opacity-5
                                    z-50
                                "
                            >
                                <div class="py-1">
                                    <div v-for="locale in availableLocales" :key="locale">
                                        <a
                                            :class="{ 'font-bold': locale === selectedLocale }"
                                            @click="changeLang(locale)"
                                            class="cursor-pointer text-sm text-gray-700 hover:bg-gray-100 flex items-center px-4 py-2"
                                        >
                                            <img
                                                :src="getIconUrl(locale)"
                                                class="w-5 h-5 lg:w-6 lg:h-6"
                                                :alt="'Ícone ' + locale"
                                            />
                                            <p class="text-sm lg:text-normal font-semibold pl-1">
                                                {{ getLocaleLabel(locale) }}
                                            </p>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </transition>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="isScrolled" :style="{ height: blueBarHeight + 'px' }"></div>

        <!-- Header mobile (parte branca) -->
        <div class="xl:hidden">
            <div
                ref="whiteBarRefMobile"
                class="flex items-center h-[65px] w-full"
                :class="isScrolled ? 'fixed top-0 left-0 z-50 bg-white shadow-lg' : ''"
            >
                <div class="container mx-auto flex justify-between items-center">
                    <button @click.stop="toggleAccordion" class="flex items-center">
                        <img
                            src="/assets/icons/header/hamburger.svg"
                            alt="Menu hamburger"
                            class="w-6 h-5 ml-5 lg:ml-20"
                        />
                    </button>
                    <div class="flex justify-center">
                        <NuxtLink to="/" class="flex items-center">
                            <img
                                src="/assets/icons/header/unochapeco.svg"
                                alt="Logo Unochapecó"
                                class="w-[155px] h-[65px] p-2"
                            />
                        </NuxtLink>
                    </div>
                    <div class="flex items-center space-x-3 mr-5 lg:mr-20">
                        <NuxtLink
                            to="https://www.unochapeco.edu.br/minhauno/"
                            rel="noopener"
                            target="_blank"
                        >
                            <img src="/assets/icons/header/user.svg" alt="Minha Uno"/>
                        </NuxtLink>
                        <NuxtLink
                            to="https://unochapeco.edu.br/minhauno/login-webmail"
                            rel="noopener"
                            target="_blank"
                        >
                            <img src="/assets/icons/header/mail.svg" alt="Webmail"/>
                        </NuxtLink>
                    </div>
                </div>
            </div>

            <transition
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 transform -translate-y-2"
                enter-to-class="opacity-100 transform translate-y-0"
                leave-active-class="transition-all duration-300 ease-in"
                leave-from-class="opacity-100 transform translate-y-0"
                leave-to-class="opacity-0 transform -translate-y-2"
            >
                <div
                    v-if="isOpen === 'block'"
                    ref="mobileDropdownRef"
                    class="fixed left-0 w-full bg-white sm:w-6/12 lg:w-4/12 shadow-lg border-t border-gray-100 z-60 ml-5 lg:ml-20"
                    :class="[
                        isScrolled ? 'top-[50px]' : 'top-[90px]',
                    ]"
                >
                    <div v-for="item in items.links" :key="item.id" class="border-b px-4 py-2">
                        <template v-if="item.link.startsWith('/')">
                            <NuxtLink :to="item.link" class="block text-[#0400A1] hover:bg-gray-100" :prefetch="true" @click="handleMenuClick">
                                {{ item.name }}
                            </NuxtLink>
                        </template>
                        <template v-else>
                            <a :href="item.link" class="block text-[#0400A1] hover:bg-gray-100" target="_blank" rel="noopener" @click="handleMenuClick">
                                {{ item.name }}
                            </a>
                        </template>
                    </div>
                    <div class="border-b px-4 py-2">
                        <a
                            href="https://www.unochapeco.edu.br/webmail"
                            class="block text-[#0400A1] hover:bg-gray-100"
                            @click="handleMenuClick"
                        >
                            Webmail
                        </a>
                    </div>
                    <div class="border-b px-4 py-2">
                        <a
                            href="https://www.unochapeco.edu.br/minhauno"
                            class="block text-[#0400A1] hover:bg-gray-100"
                            @click="handleMenuClick"
                        >
                            Minha Uno
                        </a>
                    </div>
                </div>
            </transition>
        </div>
        <!-- END Header mobile (parte branca) -->

        <!-- Menu desktop -->
        <div
            ref="whiteBarRefDesktop"
            :class="[
                'hidden xl:flex items-center h-[80px] w-full lg:h-[90px] xl:h-[120px] 2xl:h-[100px]',
                isScrolled ? 'fixed top-0 left-0 z-50 bg-white shadow-lg' : ''
            ]"
        >
            <div class="container mx-auto max-w-(--breakpoint-3xl) flex items-center px-4 md:px-8 lg:px-16 xl:px-20">
                <!-- Logo -->
                <div class="flex flex-none items-center">
                    <NuxtLink to="/" class="flex items-center">
                        <img
                            src="/assets/icons/header/unochapeco.svg"
                            alt="Logo Unochapecó"
                            class="h-[80px]"
                        />
                    </NuxtLink>
                </div>

                <!-- Menu Items -->
                <div class="flex flex-1 flex-wrap justify-center items-center space-x-2 md:space-x-4 lg:space-x-6 xl:space-x-8 my-3 xl:px-2">
                    <div v-for="item in items.links" :key="item.name" class="text-[#0400A1] text-sm font-medium hover:underline text-center py-1.5 2xl:text-base 2xl:py-0" aria-current="page">
                        <template v-if="item.link.startsWith('/')">
                            <NuxtLink :to="item.link" class="block break-normal" :prefetch="true">
                                <span v-if="item.name.split(' ').length > 1">{{ item.name.split(' ').slice(0, -1).join(' ') }}</span>
                                <span class="block">{{ item.name.split(' ').slice(-1)[0] }}</span>
                            </NuxtLink>
                        </template>
                        <template v-else>
                            <a :href="item.link" class="block break-normal" target="_blank" rel="noopener">
                                <span v-if="item.name.split(' ').length > 1">{{ item.name.split(' ').slice(0, -1).join(' ') }}</span>
                                <span class="block">{{ item.name.split(' ').slice(-1)[0] }}</span>
                            </a>
                        </template>
                    </div>
                </div>

                <div class="flex flex-none justify-end">
                    <NuxtLink
                        to="/graduacao"
                        class="cursor-pointer flex items-center justify-center rounded-full bg-linear-to-r from-[#0400A1] to-[#57D6DC] hover:shadow-lg w-[200px] h-[41px] mr-4 xl:mr-0"
                    >
                        <p class="text-white font-bold text-sm">QUERO ESTUDAR</p>
                    </NuxtLink>
                </div>
            </div>
        </div>

        <div
            v-if="isScrolled"
            :class="'hidden xl:block'"
            :style="{ height: whiteBarHeight + 'px' }"
        ></div>
    </nav>
</template>
